import React from "react";
import { Stack, Popper, autocompleteClasses, Typography, ListItemIcon, ListItemText, useTheme } from "@mui/material";
import { styledTextFieldClasses } from "../text-field/HyroTextField.styles";
import CloseIcon from "src/icons/CloseIcon";
import ChipContainer from "src/components/hyro-components/ChipContainer";
import HyroThreeDotMenu from "@hyro-components/HyroThreeDotMenu";
import CopyIcon from "src/icons/CopyIcon";
import TrashIcon from "src/icons/TrashIcon";
import FieldCopy from "src/components/widgets/FieldCopyPaste/FieldCopy.jsx";
import useHyroListField from "./HyroListField.hook.jsx";
import { StyledChipContainer, PopperContainer, StyledMenuItem, StyledAutocomplete, StyledAutocompletetInput } from "./HyroListField.styles";

const HyroListField = (
  {
    id,
    values,
    onChange,
    onDelete,
    onFocus,
    onBlur,
    label,
    required,
    options,
    helperText,
    error,
    disabled,
    index,
    isReadOnly,
    showLabel,
    isFieldReadOnlyFromSpec,
    fieldType,
    testId,
    open = false,
    maxPopperWidth,
    invalidIndexes,
    showAllChips,
    showTagsInPopper = true,
    displayTagsBelow = false,
    showCopyButton = false,
    popperOffset,
    sx,
    placeholder,
    height = "48px"
  }) => {
  const theme = useTheme();
  const {
    value,
    setValue,
    isFocused,
    anchorEl,
    popperRef,
    setIsMenuOpen,
    isCopyButtonVisible,
    handlePaste,
    handleFocus,
    handleAutoCompleteChange,
    handleCopyAll,
    handleClearAll,
    handleBlur
  } = useHyroListField({ values, onChange, onFocus, onBlur, open, showCopyButton });

  return (
    <>
      <StyledAutocomplete
        height={height}
        sx={{ ...sx, [`& .${autocompleteClasses.endAdornment}'`]: { top: "50%", transform: "translateY(-50%)" } }}
        id={id}
        options={options || []}
        value={values}
        multiple
        freeSolo
        clearOnEscape
        clearOnBlur
        clearIcon={isFocused && value.length ? <CloseIcon color="#2D2C44B2" sx={{ width: 18, height: 18 }} data-testid={`${testId ? `${testId}-` : ""}${fieldType}-${index}-clear-icon`} /> : null}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled || isReadOnly}
        showTagsInPopper={showTagsInPopper}
        onInputChange={(event, newInputValue) => {
          setValue(newInputValue);
        }}
        onChange={handleAutoCompleteChange}
        componentsProps={{
          paper: { elevation: 3 },
          popper: { sx: { paddingBottom: "12px" } },
          endAdornment: { sx: { display: "none" } }
        }}
        renderTags={() => !displayTagsBelow && (
          <>
            {(showTagsInPopper && isFocused && !isReadOnly) ?
              <Popper
                open={isFocused && Boolean(anchorEl) && values.length > 0}
                anchorEl={anchorEl}
                placement="bottom-start"
                modifiers={[{ name: "offset", options: { offset: popperOffset || [0, -16] } }]}
                sx={{
                  minWidth: anchorEl?.clientWidth,
                  maxWidth: maxPopperWidth || anchorEl?.clientWidth,
                  zIndex: (theme) => sx?.zIndex || theme.zIndex.modal + 1
                }}
              >
                <PopperContainer ref={popperRef}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Included values:</Typography>
                    <HyroThreeDotMenu
                      testId="list-field"
                      onMenuClose={() => setIsMenuOpen(false)}
                      onMenuOpen={() => setIsMenuOpen(true)}
                      sx={{ width: 28, height: 28 }} menuItems={[
                        <StyledMenuItem key={"copy-all-menu-item"} onClick={handleCopyAll}>
                          <ListItemIcon><CopyIcon size={18} /></ListItemIcon>
                          <ListItemText data-testid="list-field-copy-all-menu-item">Copy all</ListItemText>
                        </StyledMenuItem>,
                        <StyledMenuItem key={"clear-all-menu-item"} onClick={handleClearAll}>
                          <ListItemIcon><TrashIcon strokeOpacity="1" size={18} /></ListItemIcon>
                          <ListItemText data-testid="list-field-clear-all-menu-item">Clear all</ListItemText>
                        </StyledMenuItem>
                      ]} />
                  </Stack>
                  <ChipContainer
                    values={values}
                    showAllChips
                    testId={testId ? `${testId}-${fieldType}` : `${fieldType}`}
                    onDelete={onDelete}
                    isReadOnly={isReadOnly}
                    invalidIndexes={invalidIndexes} />
                </PopperContainer>
              </Popper>
              :
              <StyledChipContainer showAllChips={showAllChips}>
                <ChipContainer
                  values={values}
                  showAllChips={showAllChips}
                  testId={testId ? `${testId}-${fieldType}` : `${fieldType}`}
                  onDelete={onDelete}
                  invalidIndexes={invalidIndexes}
                  isReadOnly={isReadOnly} />
                {isCopyButtonVisible ? (
                  <span className={styledTextFieldClasses.copyIcon}>
                    <FieldCopy type={"flat"}
                      useWrapper
                      zIndex={theme.zIndex.drawer + 1}
                      value={values.join("|")}
                      iconSize={18}
                      buttonSize={32}
                      popperText="Copied all values"
                    />
                  </span>
                ) : null}
              </StyledChipContainer>}
          </>
        )}

        renderInput={params => (
          <StyledAutocompletetInput
            {...params}
            autoFocus={open}
            showAllChips={showAllChips}
            isFieldReadOnlyFromSpec={isFieldReadOnlyFromSpec}
            readOnly={isReadOnly}
            height={height}
            value={value}
            variant="outlined"
            label={showLabel ? label : ""}
            placeholder={values.length ? "" : (placeholder || label)}
            FormHelperTextProps={{ "data-testid": `${testId ? `${testId}-` : ""}${fieldType}-${index}-helper` }}
            helperText={helperText}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-testid": `${testId ? `${testId}-` : ""}${fieldType}-${index}`,
              sx: showAllChips && values?.length > 0 && showTagsInPopper && !displayTagsBelow ? { display: "none" } : {}
            }}
            required={required}
            error={!!error}
            onPaste={handlePaste}
            onKeyDown={e => {
              if (e.key === "Backspace" && !value) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
        )}
      />
      {displayTagsBelow && values?.length > 0 && (
        <StyledChipContainer showAllChips={showAllChips}>
          <ChipContainer
            values={values}
            showAllChips={showAllChips}
            testId={testId ? `${testId}-${fieldType}` : `${fieldType}`}
            onDelete={onDelete}
            invalidIndexes={invalidIndexes}
            isReadOnly={isReadOnly} />
        </StyledChipContainer>
      )}
    </>
  );
};

export default HyroListField;
